import type {
  AssessmentTemplate,
  Assessment,
  Question,
  QuestionResponse
} from '../types/assessments'
import { STATIC_LIKERT_OPTIONS } from '../types/assessments'

// Mock Assessment Templates
export const mockTemplates: AssessmentTemplate[] = [
  {
    id: 1,
    uuid: 'template-1',
    title: 'Cyber-Security Maturity',
    subtitle: 'Benchmark against NIST CSF in 15 questions',
    description:
      'Evaluate your cybersecurity posture against industry standards and identify critical gaps.',
    icon: '🛡️',
    color: '#e74c3c',
    category: 'Security',
    questions: [
      'How does your organization manage cybersecurity risk?',
      'Which NIST CSF functions are currently implemented?',
      'What is your incident response capability?'
    ],
    status: 'ACTIVE'
  },
  {
    id: 2,
    uuid: 'template-2',
    title: 'Cloud Adoption Maturity',
    subtitle: "See how you stack against the 6 R's",
    description:
      'Assess your cloud migration readiness and optimization opportunities across all workloads.',
    icon: '☁️',
    color: '#3498db',
    category: 'Cloud',
    questions: [
      'What is your current cloud adoption strategy?',
      "Which of the 6 R's migration strategies are you using?",
      'How mature is your cloud governance?'
    ],
    status: 'ACTIVE'
  },
  {
    id: 3,
    uuid: 'template-3',
    title: 'Cloud Security Maturity',
    subtitle: 'Measure your cloud security posture vs. CSA CCM',
    description: 'Measure your cloud security controls and compliance with CSA CCM framework.',
    icon: '🔒',
    color: '#9b59b6',
    category: 'Security',
    questions: [
      'How do you manage cloud access controls?',
      'What is your data encryption strategy in the cloud?',
      'How do you monitor cloud security threats?'
    ],
    status: 'ACTIVE'
  },
  {
    id: 4,
    uuid: 'template-4',
    title: 'JavaScript Fundamentals Assessment',
    subtitle: 'Test your JavaScript knowledge',
    description:
      'Comprehensive assessment covering JavaScript basics, ES6 features, and common patterns',
    icon: '💻',
    color: '#3498db',
    category: 'Programming',
    questions: [
      "What is the result of 2 + '2'?",
      'Which of the following are JavaScript data types?',
      'What is the difference between let and var?'
    ],
    status: 'ACTIVE'
  },
  {
    id: 5,
    uuid: 'template-5',
    title: 'React & TypeScript Assessment',
    subtitle: 'Advanced React concepts',
    description: 'Test your knowledge of React hooks, TypeScript, and component patterns',
    icon: '⚛️',
    color: '#61dafb',
    category: 'Frontend',
    questions: [
      'What is the purpose of useEffect hook?',
      'How do you define props in TypeScript?',
      'Explain React component lifecycle'
    ],
    status: 'ACTIVE'
  }
]

// Mock Template Questions (for preview)
const mockTemplateQuestions: Record<string, QuestionResponse[]> = {}

// Helper to get template questions for preview
export const getTemplateQuestions = (templateId: string): QuestionResponse[] => {
  if (mockTemplateQuestions[templateId]) {
    return mockTemplateQuestions[templateId]
  }

  // Generate default questions based on template
  const template = mockTemplates.find((t) => t.uuid === templateId || String(t.id) === templateId)
  if (!template) return []

  const questions: QuestionResponse[] = [
    {
      id: 1,
      uuid: `tq-${templateId}-1`,
      assessmentId: templateId,
      order: 1,
      question: template.questions[0] || 'Sample question 1',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      status: 'ACTIVE'
    },
    {
      id: 2,
      uuid: `tq-${templateId}-2`,
      assessmentId: templateId,
      order: 2,
      question: template.questions[1] || 'Sample question 2',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      status: 'ACTIVE'
    },
    {
      id: 3,
      uuid: `tq-${templateId}-3`,
      assessmentId: templateId,
      order: 3,
      question: template.questions[2] || 'Sample question 3',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      status: 'ACTIVE'
    }
  ]

  mockTemplateQuestions[templateId] = questions
  return questions
}

// Mock Assessments (created from templates)
// Using a Map-like structure to store assessments by UUID
const mockAssessments: Record<string, Assessment> = {}

// Initialize with static assessment data
const initializeMockAssessments = () => {
  const staticAssessments: Assessment[] = [
    {
      id: 1,
      uuid: 'assessment-1',
      assessmentId: 'ASSESS-001',
      title: 'Cyber-Security Maturity',
      subtitle: 'Benchmark against NIST CSF in 15 questions',
      description:
        'Evaluate your cybersecurity posture against industry standards and identify critical gaps.',
      icon: '🛡️',
      color: '#e74c3c',
      category: 'Security',
      status: 'ACTIVE',
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-15T10:00:00Z'
    },
    {
      id: 2,
      uuid: 'assessment-2',
      assessmentId: 'ASSESS-002',
      title: 'Cloud Adoption Maturity',
      subtitle: "See how you stack against the 6 R's",
      description:
        'Assess your cloud migration readiness and optimization opportunities across all workloads.',
      icon: '☁️',
      color: '#3498db',
      category: 'Cloud',
      status: 'ACTIVE',
      createdAt: '2024-01-16T10:00:00Z',
      updatedAt: '2024-01-16T10:00:00Z'
    },
    {
      id: 3,
      uuid: 'assessment-3',
      assessmentId: 'ASSESS-003',
      title: 'Cloud Security Maturity',
      subtitle: 'Measure your cloud security posture vs. CSA CCM',
      description: 'Measure your cloud security controls and compliance with CSA CCM framework.',
      icon: '🔒',
      color: '#9b59b6',
      category: 'Security',
      status: 'ACTIVE',
      createdAt: '2024-01-17T10:00:00Z',
      updatedAt: '2024-01-17T10:00:00Z'
    },
    {
      id: 4,
      uuid: 'assessment-4',
      assessmentId: 'ASSESS-004',
      title: 'JavaScript Fundamentals Assessment',
      subtitle: 'Test your JavaScript knowledge',
      description:
        'Comprehensive assessment covering JavaScript basics, ES6 features, and common patterns',
      icon: '💻',
      color: '#3498db',
      category: 'Programming',
      status: 'ACTIVE',
      createdAt: '2024-01-18T10:00:00Z',
      updatedAt: '2024-01-18T10:00:00Z'
    },
    {
      id: 5,
      uuid: 'assessment-5',
      assessmentId: 'ASSESS-005',
      title: 'React & TypeScript Assessment',
      subtitle: 'Advanced React concepts',
      description: 'Test your knowledge of React hooks, TypeScript, and component patterns',
      icon: '⚛️',
      color: '#61dafb',
      category: 'Frontend',
      status: 'ACTIVE',
      createdAt: '2024-01-19T10:00:00Z',
      updatedAt: '2024-01-19T10:00:00Z'
    }
  ]

  // Initialize mockAssessments with static data
  staticAssessments.forEach((assessment) => {
    mockAssessments[assessment.uuid] = assessment
  })
}

// Mock Questions (for assessments)
// Store questions by assessment UUID
const mockQuestions: Record<string, QuestionResponse[]> = {}

// Helper to generate mock assessment from template
export const createMockAssessment = (templateId: string, data: Partial<Assessment>): Assessment => {
  const template = mockTemplates.find((t) => t.id === parseInt(templateId))
  if (!template) {
    throw new Error('Template not found')
  }

  const assessment: Assessment = {
    id: Date.now(),
    uuid: `assessment-${Date.now()}`,
    assessmentId: `ASSESS-${Date.now()}`,
    title: data.title || template.title,
    subtitle: data.subtitle || template.subtitle,
    description: data.description || template.description,
    icon: data.icon || template.icon,
    color: data.color || template.color,
    category: data.category || template.category,
    status: 'ACTIVE',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }

  mockAssessments[assessment.uuid] = assessment
  return assessment
}

// Helper to generate mock questions
export const createMockQuestions = (assessmentId: string): QuestionResponse[] => {
  const questions: QuestionResponse[] = [
    {
      id: 1,
      uuid: 'q-1',
      assessmentId,
      order: 1,
      question: 'How does your organization manage cybersecurity risk?',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      correctAnswers: [],
      status: 'ACTIVE'
    },
    {
      id: 2,
      uuid: 'q-2',
      assessmentId,
      order: 2,
      question: 'Which NIST CSF functions are currently implemented?',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      correctAnswers: [],
      status: 'ACTIVE'
    },
    {
      id: 3,
      uuid: 'q-3',
      assessmentId,
      order: 3,
      question: 'What is your incident response capability?',
      type: 'MCQ',
      options: STATIC_LIKERT_OPTIONS,
      correctAnswers: [],
      status: 'ACTIVE'
    }
  ]

  mockQuestions[assessmentId] = questions
  return questions
}

// Get mock questions for an assessment
export const getMockQuestions = (assessmentId: string): QuestionResponse[] => {
  if (!mockQuestions[assessmentId]) {
    return createMockQuestions(assessmentId)
  }
  return mockQuestions[assessmentId]
}

// Get mock assessment by ID (tries UUID first, then ID)
export const getMockAssessment = (id: string): Assessment | null => {
  // Try UUID first
  if (mockAssessments[id]) {
    return mockAssessments[id]
  }
  // Try finding by ID
  const found = Object.values(mockAssessments).find((a) => String(a.id) === id || a.uuid === id)
  return found || null
}

// Get all mock assessments
export const getAllMockAssessments = (): Assessment[] => {
  // Initialize if empty (first call)
  if (Object.keys(mockAssessments).length === 0) {
    initializeMockAssessments()
    // Initialize questions for each assessment
    Object.values(mockAssessments).forEach((assessment) => {
      if (!mockQuestions[assessment.uuid]) {
        createMockQuestions(assessment.uuid)
      }
    })
  }
  return Object.values(mockAssessments)
}

// Delete mock assessment
export const deleteMockAssessment = (id: string): boolean => {
  // Try UUID first
  if (mockAssessments[id]) {
    delete mockAssessments[id]
    // Also delete associated questions
    if (mockQuestions[id]) {
      delete mockQuestions[id]
    }
    return true
  }
  // Try finding by ID
  const found = Object.values(mockAssessments).find((a) => String(a.id) === id || a.uuid === id)
  if (found) {
    delete mockAssessments[found.uuid]
    if (mockQuestions[found.uuid]) {
      delete mockQuestions[found.uuid]
    }
    return true
  }
  return false
}

// Update mock assessment
export const updateMockAssessment = (id: string, data: Partial<Assessment>): Assessment | null => {
  const assessment = getMockAssessment(id)
  if (!assessment) {
    return null
  }
  const updated = {
    ...assessment,
    ...data,
    updatedAt: new Date().toISOString()
  }
  // Update in the mockAssessments record
  mockAssessments[assessment.uuid] = updated
  return updated
}

// Add mock question
export const addMockQuestion = (
  assessmentId: string,
  question: Omit<Question, 'id' | 'uuid'>
): QuestionResponse => {
  const questions = getMockQuestions(assessmentId)
  const newQuestion: QuestionResponse = {
    id: Date.now(),
    uuid: `q-${Date.now()}`,
    assessmentId,
    order: question.order,
    question: question.question,
    type: question.type,
    options: STATIC_LIKERT_OPTIONS,
    correctAnswers: question.correctAnswers,
    status: question.status || 'ACTIVE'
  }

  questions.push(newQuestion)
  mockQuestions[assessmentId] = questions
  return newQuestion
}

// Update mock question
export const updateMockQuestion = (
  assessmentId: string,
  questionId: number,
  updates: Partial<Question>
): QuestionResponse | null => {
  const questions = getMockQuestions(assessmentId)
  const index = questions.findIndex((q) => q.id === questionId)
  if (index === -1) return null

  questions[index] = { ...questions[index], ...updates }
  mockQuestions[assessmentId] = questions
  return questions[index]
}

// Delete mock question
export const deleteMockQuestion = (assessmentId: string, questionId: number): boolean => {
  const questions = getMockQuestions(assessmentId)
  const filtered = questions.filter((q) => q.id !== questionId)
  if (filtered.length === questions.length) return false

  mockQuestions[assessmentId] = filtered
  return true
}

// Reorder mock questions
export const reorderMockQuestions = (
  assessmentId: string,
  orders: Array<{ questionId: number; order: number }>
): void => {
  const questions = getMockQuestions(assessmentId)
  const questionMap = new Map(questions.map((q) => [q.id, q]))

  orders.forEach(({ questionId, order }) => {
    const question = questionMap.get(questionId)
    if (question) {
      question.order = order
    }
  })

  questions.sort((a, b) => a.order - b.order)
  mockQuestions[assessmentId] = questions
}
